import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { adRulesService, AdRulePlatform, CampaignMetricsRow } from '@/services/ad-rules.service';
import { facebookCampaignsService } from '@/services/facebook-campaigns.service';
import { tiktokService } from '@/services/tiktok.service';
import { Scale, Trophy, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

interface CampaignOption { id: string; name: string }

/** Lower is better for cost metrics; higher is better for the rest. */
const LOWER_IS_BETTER: Record<string, boolean> = { spend: true, cpl: true, cpc: true };

type MoneyKey = 'spend' | 'cpc' | 'cpl';
const CONVERTED: Record<MoneyKey, keyof CampaignMetricsRow> = { spend: 'spendConverted', cpc: 'cpcConverted', cpl: 'cplConverted' };

const METRIC_ROWS: Array<{ key: keyof CampaignMetricsRow; label: string; money?: boolean; format: (v: number) => string }> = [
  { key: 'spend', label: 'Spend', money: true, format: (v) => v.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
  { key: 'impressions', label: 'Impressions', format: (v) => v.toLocaleString() },
  { key: 'clicks', label: 'Clicks', format: (v) => v.toLocaleString() },
  { key: 'ctr', label: 'CTR %', format: (v) => v.toFixed(2) },
  { key: 'cpc', label: 'Cost / click', money: true, format: (v) => v.toFixed(2) },
  { key: 'leads', label: 'Leads', format: (v) => v.toLocaleString() },
  { key: 'cpl', label: 'Cost / lead', money: true, format: (v) => v.toFixed(2) },
];

export default function AdsCompare() {
  const [platform, setPlatform] = useState<AdRulePlatform>('meta');
  const [options, setOptions] = useState<CampaignOption[]>([]);
  const [picked, setPicked] = useState<string[]>(['', '']);
  const [lookback, setLookback] = useState(7);
  const [rows, setRows] = useState<CampaignMetricsRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOptions([]);
    setPicked(['', '']);
    setRows(null);
    (async () => {
      try {
        if (platform === 'meta') {
          const r = await facebookCampaignsService.list(false, 100);
          setOptions(r.data.map((c) => ({ id: c.id, name: c.name })));
        } else {
          const r = await tiktokService.listCampaigns();
          setOptions(r.map((c) => ({ id: c.tiktokCampaignId, name: c.campaignName })));
        }
      } catch { /* interceptor surfaces */ }
    })();
  }, [platform]);

  const chosenIds = useMemo(() => picked.filter(Boolean), [picked]);
  const nameById = useMemo(() => new Map(options.map((o) => [o.id, o.name])), [options]);

  const run = async () => {
    if (chosenIds.length < 2) { toast.error('Pick at least two campaigns to compare'); return; }
    setLoading(true);
    try {
      const result = await adRulesService.compare(platform, chosenIds, lookback);
      // Preserve the user's pick order; campaigns with no cached rows show zeros.
      setRows(chosenIds.map((id) =>
        result.find((r) => r.entityId === id)
        ?? { entityId: id, spend: 0, impressions: 0, clicks: 0, leads: 0, cpl: null, cpc: null, ctr: null },
      ));
    } catch (e: any) {
      // axios interceptor already toasted backend message
    } finally { setLoading(false); }
  };

  const winnerFor = (key: keyof CampaignMetricsRow): string | null => {
    if (!rows) return null;
    const candidates = rows.filter((r) => r[key] !== null && r[key] !== undefined) as Array<CampaignMetricsRow & Record<string, number>>;
    if (candidates.length < 2) return null;
    const sorted = [...candidates].sort((a, b) =>
      LOWER_IS_BETTER[key as string] ? (a[key] as number) - (b[key] as number) : (b[key] as number) - (a[key] as number),
    );
    // A tie has no winner.
    if (sorted[0][key] === sorted[1][key]) return null;
    return sorted[0].entityId;
  };

  return (
    <>
      <Topbar
        title="A/B Compare"
        breadcrumb="Ads / A/B Compare"
        description="Run two versions, keep the winner. Compare campaigns side by side on synced metrics."
      />

      <div className="w-full px-7 py-7 space-y-4">
        <Card padded>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">Platform</label>
              <Select value={platform} onChange={(e) => setPlatform(e.target.value as AdRulePlatform)}>
                <option value="meta">Meta (FB + IG)</option>
                <option value="tiktok">TikTok</option>
              </Select>
            </div>
            {picked.map((p, i) => (
              <div key={i}>
                <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">Campaign {String.fromCharCode(65 + i)}</label>
                <Select value={p} onChange={(e) => setPicked((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))}>
                  <option value="">Pick…</option>
                  {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              </div>
            ))}
            {picked.length < 4 && (
              <Button variant="secondary" onClick={() => setPicked((p) => [...p, ''])}>+ Add</Button>
            )}
            <div>
              <label className="mb-1 block text-2xs font-medium uppercase tracking-wide text-text-muted">Window</label>
              <Select value={String(lookback)} onChange={(e) => setLookback(parseInt(e.target.value, 10))}>
                {[3, 7, 14, 30].map((d) => <option key={d} value={d}>Last {d} days</option>)}
              </Select>
            </div>
            <Button onClick={run} loading={loading} iconLeft={<RefreshCw size={13} />}>Compare</Button>
          </div>
        </Card>

        {!rows ? (
          <EmptyState
            icon={<Scale size={40} className="text-text-muted" />}
            title="Pick campaigns to compare"
            description="Choose two to four campaigns and a window. Winners per metric get the trophy."
          />
        ) : (
          <Card padded={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-2.5 text-2xs font-medium uppercase tracking-wide text-text-muted">Metric</th>
                  {rows.map((r, i) => (
                    <th key={r.entityId} className="px-4 py-2.5">
                      <div className="text-xs font-semibold text-text-primary truncate max-w-[180px]">
                        {String.fromCharCode(65 + i)} · {nameById.get(r.entityId) ?? r.entityId}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {METRIC_ROWS.map((m) => {
                  const winner = winnerFor(m.key);
                  return (
                    <tr key={m.key}>
                      <td className="px-4 py-2.5 text-xs font-medium text-text-secondary">{m.label}</td>
                      {rows.map((r) => {
                        const v = r[m.key] as number | null;
                        const isWinner = winner === r.entityId;
                        return (
                          <td key={r.entityId} className={clsx('px-4 py-2.5 tabular-nums', isWinner ? 'font-semibold text-success' : 'text-text-primary')}>
                            <span className="inline-flex items-center gap-1">
                              {v === null || v === undefined ? '—' : `${m.money && r.currency ? `${r.currency} ` : ''}${m.format(v)}`}
                              {isWinner && <Trophy size={11} className="text-success" />}
                            </span>
                            {m.money && v != null && r.companyCurrency && r.currency && r.currency !== r.companyCurrency && r[CONVERTED[m.key as MoneyKey]] != null && (
                              <span className="block text-2xs font-normal text-text-muted">
                                ≈ {r.companyCurrency} {m.format(r[CONVERTED[m.key as MoneyKey]] as number)}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="border-t border-border px-4 py-2 text-2xs text-text-muted">
              {rows[0]?.currency ? `Money is in ${rows[0].currency}, the ad account's billing currency${rows[0].companyCurrency && rows[0].companyCurrency !== rows[0].currency ? `; ≈ ${rows[0].companyCurrency} at today's rate underneath` : ''}. ` : ''}
              Metrics come from your synced insight snapshots — sync the platform's reporting first if numbers look empty.
              “—” means no signal yet (e.g. cost per lead with zero leads).
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
